"use client";

import { useState, useMemo, useEffect } from "react";
import {
  format,
  getDaysInMonth,
  startOfMonth,
  addMonths,
  subMonths,
} from "date-fns";
import { ChevronLeft, ChevronRight, Clock, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FacilitySlot } from "@/types/facility/booking";
import { useToast } from "@/components/ui/use-toast";
import { Lock } from "lucide-react";

interface SlotSelectorProps {
  slots: FacilitySlot[];
  selectedSlot: FacilitySlot | null;
  onSlotSelect: (slot: FacilitySlot) => void;
  isLoading?: boolean;
  action?: "add" | "edit";
  cartedSlotIds?: number[];
  disabled?: boolean;
  onMonthChange?: (month: Date) => void;
}

// ── Unique slot key: slot_date + start_time + end_time ─────────────────────
// We cannot rely on `id` because the API may return 0/null for all slots.
const slotKey = (slot: FacilitySlot) =>
  `${slot.slot_date}__${slot.start_time}__${slot.end_time}`;

export function SlotSelector({
  slots,
  selectedSlot,
  onSlotSelect,
  isLoading = false,
  action = "add",
  cartedSlotIds = [],
  disabled = false,
  onMonthChange,
}: SlotSelectorProps) {
  const { toast } = useToast();
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"calendar" | "slots">("calendar");

  // Initialize component state when dropdown opens
  useEffect(() => {
    if (isOpen) {
      // Only set currentMonth if not already set or if it's the initial open
      // We check if selectedSlot exists to center the calendar on it
      if (selectedSlot && slots.length > 0) {
        const foundSlot = slots.find(
          (slot) => slotKey(slot) === slotKey(selectedSlot),
        );
        if (foundSlot) {
          try {
            const slotDate = format(new Date(foundSlot.slot_date), "yyyy-MM-dd");
            setSelectedDate(slotDate);
            // Also update currentMonth to show the month of the selected slot
            setCurrentMonth(new Date(foundSlot.slot_date));
            setViewMode("slots");
            return; // Exit early as we've initialized
          } catch (error) {
            console.warn("Error formatting slot date:", error);
          }
        }
      }

      // Default: If no valid selection or logic above didn't return, 
      // just ensure we have A month set if none is set yet
      // But DON'T reset if we are already open and navigating
    }
  }, [isOpen]); // Only trigger on open change

  // Reset state when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedDate("");
      setViewMode("calendar");
    }
  }, [isOpen]);

  const slotsByDate = useMemo(() => {
    const map: Record<string, FacilitySlot[]> = {};
    const validSlots = slots
      .filter((slot) => {
        // Additional validation to ensure slot has all required fields
        if (!slot.slot_date || !slot.start_time || !slot.end_time) {
          console.warn("Slot missing required fields:", slot);
          return false;
        }

        // Validate date format - slot.slot_date is already in ISO format like "2025-10-26T00:00:00"
        try {
          const testDate = new Date(slot.slot_date);
          if (isNaN(testDate.getTime())) {
            console.warn("Slot with invalid date:", slot.slot_date);
            return false;
          }
          return true;
        } catch {
          return false;
        }
      });

    validSlots.forEach((slot) => {
      try {
        const dateToFormat = new Date(slot.slot_date);
        if (isNaN(dateToFormat.getTime())) return;
        const dateKey = format(dateToFormat, "yyyy-MM-dd");
        if (!map[dateKey]) map[dateKey] = [];
        map[dateKey].push(slot);
      } catch {
        // skip invalid slots
      }
    });

    return map;
  }, [slots, action]);

  const slotsForSelectedDate = useMemo(() => {
    if (!selectedDate) return [];
    let dateSlots = slotsByDate[selectedDate] || [];

    if (dateSlots.length === 0 && selectedSlot && action === "edit") {
      try {
        const selectedSlotDate = format(
          new Date(selectedSlot.slot_date),
          "yyyy-MM-dd",
        );
        if (selectedSlotDate === selectedDate) {
          dateSlots = [selectedSlot];
        }
      } catch {
        // ignore
      }
    }

    return dateSlots;
  }, [selectedDate, slotsByDate, selectedSlot, action]);

  const calendarDays = useMemo(() => {
    const firstDay = startOfMonth(currentMonth);
    const daysInMonth = getDaysInMonth(currentMonth);
    const startingDayOfWeek = firstDay.getDay();

    const days: (Date | null)[] = [];
    for (let i = 0; i < startingDayOfWeek; i++) days.push(null);
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(
        new Date(currentMonth.getFullYear(), currentMonth.getMonth(), i),
      );
    }
    return days;
  }, [currentMonth]);

  const handlePrevMonth = () => {
    const newMonth = subMonths(currentMonth, 1);
    setCurrentMonth(newMonth);
    setSelectedDate("");
    setViewMode("calendar");
    onMonthChange?.(newMonth);
  };

  const handleNextMonth = () => {
    const newMonth = addMonths(currentMonth, 1);
    setCurrentMonth(newMonth);
    setSelectedDate("");
    setViewMode("calendar");
    onMonthChange?.(newMonth);
  };

  const handleDateClick = (date: Date) => {
    try {
      const dateKey = format(date, "yyyy-MM-dd");
      setSelectedDate(dateKey);
      setViewMode("slots");
    } catch (error) {
      console.warn("Error handling date click:", date, error);
    }
  };

  const handleBackToCalendar = () => setViewMode("calendar");

  const handleSlotSelect = (slot: FacilitySlot) => {
    if (!slot.is_available) {
      toast({
        variant: "destructive",
        title: "Slot Unavailable",
        description: slot.unavailability_reason || "This slot is not available for booking",
      });
      return;
    }
    onSlotSelect(slot);
    setIsOpen(false);
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-between text-xs h-9 bg-transparent px-3 text-left font-medium",
            disabled && "opacity-50 cursor-not-allowed bg-gray-50",
          )}
          disabled={disabled || isLoading}
        >
          <span className="truncate">
            {selectedSlot
              ? (() => {
                  try {
                    return `${format(new Date(selectedSlot.slot_date), "EEEE, MMM dd, yyyy")} at ${selectedSlot.start_time || "TBD"} - ${selectedSlot.end_time || "TBD"}`;
                  } catch {
                    return "Invalid date selected";
                  }
                })()
              : "Select a time slot"}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="start"
        className="w-72 p-3 min-h-[300px]"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <div className="space-y-3 relative">
          {isLoading && (
            <div className="absolute inset-0 bg-white/50 z-50 flex items-center justify-center backdrop-blur-[1px]">
              <div className="text-[10px] font-medium text-gray-400 animate-pulse">
                Updating slots...
              </div>
            </div>
          )}

          {viewMode === "calendar" ? (
            <>
              {/* Month Navigation */}
              <div className="flex items-center justify-between">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handlePrevMonth}
                  className="h-6 w-6 p-0"
                >
                  <ChevronLeft className="h-3 w-3" />
                </Button>
                <h2 className="text-xs font-medium">
                  {format(currentMonth, "MMM yyyy")}
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleNextMonth}
                  className="h-6 w-6 p-0"
                >
                  <ChevronRight className="h-3 w-3" />
                </Button>
              </div>

              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1">
                {["S", "M", "T", "W", "T", "F", "S"].map((day, i) => (
                  <div
                    key={`hdr-${i}`}
                    className="text-center text-xs font-medium text-gray-500 py-1"
                  >
                    {day}
                  </div>
                ))}

                {/* Calendar cells */}
                {calendarDays.map((date, idx) => {
                  if (!date) {
                    return <div key={`empty-${idx}`} className="h-8" />;
                  }

                  const dateKey = format(date, "yyyy-MM-dd");
                  const dateSlots = slotsByDate[dateKey] || [];
                  const hasSlots = dateSlots.length > 0;
                  const allUnavailable = hasSlots && dateSlots.every((s) => !s.is_available);
                  const isSelected = selectedDate === dateKey;

                  const isSelectedSlotDate = selectedSlot
                    ? (() => {
                        try {
                          return (
                            format(new Date(selectedSlot.slot_date), "yyyy-MM-dd") ===
                            dateKey
                          );
                        } catch {
                          return false;
                        }
                      })()
                    : false;

                  return (
                    <button
                      key={dateKey}
                      onClick={() => handleDateClick(date)}
                      disabled={!hasSlots}
                      className={cn(
                        "h-8 rounded text-xs font-medium transition-all flex items-center justify-center relative min-w-0",
                        !hasSlots &&
                          "bg-gray-100 text-gray-300 cursor-not-allowed",
                        (isSelected || isSelectedSlotDate) &&
                          "ring-2 ring-blue-500 bg-blue-100 text-blue-900",
                        hasSlots &&
                          !isSelected &&
                          !isSelectedSlotDate &&
                          "border border-gray-200 hover:bg-blue-50 text-gray-900 cursor-pointer",
                      )}
                    >
                      <span className="leading-none">{date.getDate()}</span>
                      {hasSlots && (
                        <span
                          className={cn(
                            "absolute bottom-0.5 right-0.5 w-1.5 h-1.5 rounded-full",
                            allUnavailable ? "bg-red-400" : "bg-blue-500",
                          )}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <>
              {/* Slots view header */}
              <div className="flex items-center gap-3 mb-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBackToCalendar}
                  className="h-7 w-7 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  {(() => {
                    try {
                      return format(new Date(selectedDate), "EEEE, MMM dd, yyyy");
                    } catch {
                      return "Invalid date";
                    }
                  })()}
                </div>
              </div>

              {/* Slot list */}
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {slotsForSelectedDate.length === 0 ? (
                  <div className="text-center py-8 text-xs text-gray-400 font-medium">
                    No slots available for this date
                  </div>
                ) : (
                  slotsForSelectedDate.map((slot, idx) => {
                    // ── KEY FIX: use composite key, not id ──────────────────
                    const key = slotKey(slot);
                    const isThisSlotSelected =
                      selectedSlot !== null && slotKey(selectedSlot) === key;
                    const isInCart = cartedSlotIds.includes(slot.id);
                    const isUnavailable = !slot.is_available;

                    return (
                      <button
                        key={`${key}-${idx}`}
                        onClick={() => !isInCart && handleSlotSelect(slot)}
                        className={cn(
                          "w-full text-left p-3 rounded-md text-xs transition-all border flex flex-col gap-1",
                          isThisSlotSelected
                            ? "border-blue-500 bg-blue-50 font-medium text-blue-900"
                            : isInCart || isUnavailable
                            ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed opacity-70"
                            : "border-gray-200 hover:border-blue-300 hover:bg-gray-50 text-gray-900",
                          isUnavailable && "grayscale-[0.5]",
                        )}
                      >
                        <div className="font-medium flex items-center justify-between w-full">
                          <span className="flex-1 text-left min-w-0 overflow-hidden text-ellipsis flex items-center gap-2">
                            {slot.start_time || "TBD"} - {slot.end_time || "TBD"}
                            {isInCart && (
                              <span className="text-[10px] bg-gray-200 text-gray-600 px-1.5 py-0.5 rounded uppercase font-bold">
                                In Cart
                              </span>
                            )}
                            {isUnavailable && (
                              <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded uppercase font-bold flex items-center gap-1">
                                <Lock className="h-2.5 w-2.5" />
                                {slot.unavailability_reason || "Unavailable"}
                              </span>
                            )}
                          </span>
                          {slot.is_exclusive_schedule && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full ml-2 flex-shrink-0">
                              Exclusive
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 flex items-center justify-between w-full">
                          <span>
                            Duration:{" "}
                            {slot.duration_hours ??
                              (Number(slot.end_time.split(":")[0]) * 60 +
                                Number(slot.end_time.split(":")[1]) -
                                (Number(slot.start_time.split(":")[0]) * 60 +
                                  Number(slot.start_time.split(":")[1]))) /
                                60}
                            h
                          </span>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}